import React, { useMemo, useState } from 'react';

import { doRequest } from '@r1-frontend/do-request';
import { IEverywhereWatchItem } from '@r1-frontend/api-domru/api-profile/v1/domru-tv/everywhere-watch-list';
import {
    activateWatchEverywhere,
    IActivateWatchEverywhereResponseDto,
} from '@r1-frontend/api-domru/api-profile/v1/domru-tv/everywhere-watch-on';

import Popup from './popup';

import * as ST from './styled';

interface IProps {
    onConnect: () => Promise<void>,
    tariff: IEverywhereWatchItem,
}

const TariffCardControl = ({ onConnect, tariff }: IProps) => {
    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const [errorText, setErrorText] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    const preparedTariff = useMemo(() => {
        return {
            serviceId: tariff.serviceId,
            dateFrom: tariff.dateFromFormatted,
            dateTo: tariff.dateToFormatted,
            tvSlotsCount: tariff.tvSlotsCount,
            label: tariff.serviceName.split(' ').slice(-1),
            isActive: !!tariff.status,
            title: tariff.serviceName,
            price: tariff.currentPrice,
        };
    }, [tariff]);

    const devicesText = useMemo(
        () => preparedTariff.tvSlotsCount === 1 ? 'любом устройстве' : 'любых устройствах',
        [preparedTariff],
    );

    const popupClose = () => {
        setIsPopupOpen(false);
        setErrorText(null);
    };

    const connectTariff = async() => {
        setIsLoading(true);

        const resp = await doRequest.apiProfile<IActivateWatchEverywhereResponseDto>(
            activateWatchEverywhere({ serviceId: preparedTariff.serviceId }),
        );

        if (resp.isSuccess) {
            onConnect();
            popupClose();
        } else {
            setErrorText(resp.error.message);
        }

        setIsLoading(false);
    };

    return (
        <ST.TariffCard>
            <ST.TariffInfoWrapper>
                <ST.TariffTitleWrapper>
                    <ST.TariffTitle
                        isActive={preparedTariff.isActive}
                    >
                        {preparedTariff.title}
                    </ST.TariffTitle>
                    <ST.TariffLabel
                        data-test='tariff-label'
                        isActive={preparedTariff.isActive}
                    >
                        {preparedTariff.label}
                    </ST.TariffLabel>

                    <ST.TariffDate data-test='tariff-date'>
                        {prepareActivePeriod(preparedTariff.dateFrom, preparedTariff.dateTo)}
                    </ST.TariffDate>
                </ST.TariffTitleWrapper>

                <ST.TariffDescription>
                    {
                        preparedTariff.serviceId === -1
                            ? <>Подключается автоматически и&nbsp;бесплатно всем Клиентам Дом.ру с&nbsp;услугой &laquo;ТВ&raquo;,&nbsp;без возможности отключения.<br/>
                                Смотрите ТВ-каналы и&nbsp;подписки на&nbsp;{preparedTariff.tvSlotsCount}&nbsp;телевизоре&nbsp;и&nbsp;4&nbsp;устройствах: смартфоне,
                                планшете или через браузер на&nbsp;ноутбуке или компьютере.</>
                            : <>Смотрите ТВ-каналы и&nbsp;подписки на&nbsp;{preparedTariff.tvSlotsCount}&nbsp;{devicesText}. Ускорение интернета до&nbsp;100&nbsp;Мбит/с
                                в&nbsp;подарок (при подключении тарифа без скидок).</>
                    }
                </ST.TariffDescription>
            </ST.TariffInfoWrapper>

            <ST.TariffConnectWrapper>
                <ST.TariffPrice data-test='tariff-price'>{preparedTariff.price} ₽/мес</ST.TariffPrice>

                <ST.TariffPriceNote>
                    Продлевается автоматически.
                    {
                        !preparedTariff.isActive && <>
                            <br/>Абонентская плата спишется с вашего счета.
                        </>
                    }
                </ST.TariffPriceNote>

                {
                    !preparedTariff.isActive && <ST.Button
                        type={ST.BUTTON_TYPE_PRIMARY}
                        onClick={() => setIsPopupOpen(true)}
                        data-test='tariff-button'
                    >
                        Подключить за {preparedTariff.price} ₽/мес
                    </ST.Button>
                }
            </ST.TariffConnectWrapper>

            <Popup
                errorText={errorText}
                isLoading={isLoading}
                isOpen={isPopupOpen}
                onConnectTariff={connectTariff}
                onErrorRead={() => setErrorText(null)}
                onPopupClose={popupClose}
                price={preparedTariff.price}
                title={preparedTariff.title}
            />
        </ST.TariffCard>
    );
};

const prepareActivePeriod = (dateFrom?: string, dateTo?: string) => {
    const acc = [];

    if (dateFrom) {
        acc.push(`активен с ${dateFrom}`);
    }

    if (dateTo) {
        acc.push(dateFrom ? ' по ' : 'активно до ');
        acc.push(dateTo);
    }

    return acc.join('');
};

export default TariffCardControl;
